import { setFirstLoginViewed } from '@/services/api/resources/organization/actions';
import { apiErrorMessage, box, container, loadingButton, namedLink, pageSubtitle } from '@/ui/components';
import { isFirstOrganizationUser } from '@/ui/helpers';
import { h, defineComponent, ref, computed } from 'vue';
import { inputField } from '@/ui/components/form';
import { login } from '@/ui/auth/auth';
import { locales, t } from '@/ui/i18n/i18n';
import { useSetLocale, useTask, useValidation } from '@/ui/concerns';
import { LoggingInUser, validate } from '@/entities/auth/loggingInUser';
import { StatusCodes } from 'http-status-codes';
import { pushRoute } from '@/ui/helpers/uiHelper';
import { AuthIllustration } from '@/ui/components/auth/AuthIllustrations';
import { router, useCurrentRoute } from '@/ui/routes';
import { ReCaptcha } from '@/ui/components/auth/ReCaptcha';
import { v4 as uuidv4 } from 'uuid';
import { isSsoEnabled } from '@/ui/helpers/featureHelper';

interface LocalePageProps {
  locale?: locales;
}

export const LoginPage = defineComponent<LocalePageProps>({
  name: 'LoginPage',
  props: ['locale'],
  setup(props) {
    useSetLocale(props.locale);

    const currentRoute = useCurrentRoute();

    const user = ref<LoggingInUser>({
      email: '',
      password: '',
    });

    const errorCounter = ref<number>(0);

    // show google recaptcha after three failed submission
    const showReCaptcha = computed(() => {
      return process.env.VUE_APP_GOOGLE_RECAPTCHA_SITE_KEY
        && errorCounter.value > 2;
    });

    const isReCaptchaVerified = ref<boolean>(false);

    const {
      run: submitForm,
      state,
    } = useTask(
      () => login(user.value),
      loggedInUser => {
        useSetLocale(loggedInUser.locale);
        if (currentRoute.value.query.from && currentRoute.value.query.from !== currentRoute.value.path) {
          router.replace(currentRoute.value.query.from as unknown as string);
        } else if (!loggedInUser.organization.firstLoginViewed && isFirstOrganizationUser(loggedInUser)) {
          setFirstLoginViewed(loggedInUser.organization.id)();
          pushRoute({ name: 'checklistChooseMethod', query: { first: 'true' } });
        } else {
          pushRoute({ name: 'home' });
        }
      },
      uuidv4(),
      error => {
        if (error.code === StatusCodes.UNAUTHORIZED || error.code === StatusCodes.UNPROCESSABLE_ENTITY) {
          errorCounter.value++;
        }
      },
    );

    const {
      validationErrors,
      hasNoValidationErrors,
      showValidationErrors,
    } = useValidation(validate, user, state);

    const canSubmitForm = () => {
      return hasNoValidationErrors.value && (isReCaptchaVerified.value || !showReCaptcha.value);
    };

    const handleSignIn = () => {
      showValidationErrors.value = true;
      if (canSubmitForm()) {
        submitForm();
        isReCaptchaVerified.value = false;
      }
    };

    return function render() {
      const fields = [
        inputField({
          name: 'email',
          type: 'email',
          label: t('common.email'),
          placeholder: t('auth.enter-email'),
          value: user.value.email,
          required: false,
          errors: validationErrors.value,
          disabled: state.value.pending,
        }, v => {
          user.value.email = v;
        }),
        inputField({
          name: 'password',
          type: 'password',
          label: t('auth.password'),
          placeholder: t('auth.enter-password'),
          value: user.value.password,
          required: false,
          errors: validationErrors.value,
          disabled: state.value.pending,
        }, v => {
          user.value.password = v;
        }),
      ];

      return container(
        [
          box([h('form', {
            on: {
              keyup: (e: KeyboardEvent) => e.key === 'Enter' && handleSignIn(),
            },
          }, [
            pageSubtitle(t('auth.sign-in-account')),
            ...fields,
            h(ReCaptcha, {
              props: {
                show: showReCaptcha.value && !state.value.pending,
              },
              on: {
                input: (value: boolean) => {
                  isReCaptchaVerified.value = value;
                },
              },
            }),
            apiErrorMessage(state.value, { [StatusCodes.UNAUTHORIZED]: t('message.login-error') }),
            h(
              'div',
              { class: 'field' },
              [loadingButton(t('auth.sign-in'), handleSignIn, state.value.pending, { expanded: true })],
            ),
            h('div', [namedLink(t('auth.password-forgot'), 'initiatePasswordReset')]),
          ])]),
          isSsoEnabled() ? h('div', { class: 'has-text-centered mb-2' }, [
            namedLink(t('auth.sign-in-account-sso'), 'ssoLogin'),
          ]) : [],
          h(AuthIllustration),
        ],
        'small',
        'px-8 pb-8 pt-10',
      );
    };
  },
});
